// API Security — Event detail drawer (Phase 1B).
//
// Rewrites the original "row-bag-only" drawer into a SmartParse-class
// experience: a per-section card layout backed by the new CP endpoint
// GET /api/user/v1/asec/:site_uid/events/:request_id, which returns a
// `{primary_stream, primary, context}` envelope unioning the four
// underlying CH streams plus the DLP audit table.
//
// Sections (all collapsible, rendered only when their data is present):
//   • Event Summary            — chips + canned explanation hint
//   • What happened            — canned eventMeta.what_happened
//   • Recommended action       — canned eventMeta.recommendations[]
//   • Request Details          — host/method/path + UA + JA3/JA4 +
//                                query keys best-effort
//   • Response Details         — status + size + latency + content type
//   • Identity                 — identity_kind/_id/_idp when present
//   • Matched Spec Operation   — spec_id + op id + template + method
//   • GraphQL Operation        — op_type/op_name + depth/complexity/
//                                aliases/root_fields
//   • PII Detected             — DLP audit rows for this request_id
//   • Threat Context           — ASN + country + src IP
//   • Similar Events           — 4 deep-link buttons into Events.js
//   • Raw Event                — collapsed JSON of the full envelope
//
// The drawer fetches its data on open. State machine:
//   loading  → CircularProgress
//   found    → all sections render conditionally
//   404      → single "Event not found" card showing the request_id
//   error    → single "Could not load event" card
//
// Caller contract (unchanged from the prior shape):
//   <AsecEventDrawer
//      open={bool}
//      onClose={fn}
//      event={ev}                  // the table row that was clicked
//      siteId={uuid}               // tenant scope for fetch + deep links
//      categoryMeta={CATEGORY_META}// optional — header icon colour
//   />

import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Button,
  Chip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import copy from "copy-to-clipboard";
import {
  Lock,
  FileWarning,
  Code2,
  Activity,
  ShieldAlert,
  KeyRound,
  FileOutput,
  FileInput,
  FileText,
  Lightbulb,
} from "lucide-react";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";

import asec from "../../../../../utils/axios/v1/asecAxios";
import colors from "../../../../../utils/colors";
import { formatDate } from "../../../../../utils/format";
import LayerdIcon from "../../../../common/LayerdIcon";
import { DETECTION_ICONS } from "../../../../../icons/detectionLucideIcons";
import WafEventDrawerShell from "../../detection/WafEventDrawerShell";
import SmartParseDrawerHeader from "../../detection/v3/SmartParseDrawerHeader";
import SmartParseRawJsonViewer from "../../detection/v3/SmartParseRawJsonViewer";
import { EventAssessGrid, SmartParseSectionCard as SectionCard, DrawerFieldStack, DrawerNumberedList, NetworkInformationPanel, NetworkCountryFlagRow, IpReputationPanel, spSecondaryBodySx, toDrawerFieldValue, drawerFieldsHaveContent, hasIpReputationEnrichment } from "../../detection/v3/smartParseDrawerUi";
import { Clock, Tag, AlertCircle, FileWarning as FileWarningIcon } from "lucide-react";
import {
  PRIMARY_COLOR,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
  BORDER_COLOR,
  secondaryButtonStyle,
  tagChipSx,
} from "../../detection/wafEventDrawerShared";
import { lookupEventMeta, SEVERITY_PALETTE } from "./eventMeta";

// ─── Helpers ─────────────────────────────────────────────────────────
const fmtMs = (us) => {
  if (us === undefined || us === null || us === "") return "—";
  const n = Number(us);
  if (!Number.isFinite(n) || n <= 0) return "—";
  const ms = n / 1000;
  if (ms < 1) return "<1 ms";
  if (ms < 1000) return `${ms.toFixed(0)} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
};

const fmtBytes = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v) || v <= 0) return "—";
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
  return `${(v / (1024 * 1024)).toFixed(1)} MB`;
};

const nonEmpty = (v) =>
  v !== undefined && v !== null && v !== "" && v !== 0;

// `request_headers_json` / `response_headers_json` arrive on every CH
// stream as a DLP-redacted JSON object: keys are header names, values
// are either the original token or the literal "[REDACTED]" string
// (denylist headers like Authorization are dropped wholesale before
// the JSON is built so they will never appear at all).
//
// Returns `{ rows: [[k,v]…], rawFallback: string|null }`. If the
// input doesn't parse as a JSON object, we hand back the raw string
// for the caller to display in a <pre>.
function parseHeadersJson(raw) {
  if (!raw || typeof raw !== "string") return { rows: [], rawFallback: null };
  try {
    const obj = JSON.parse(raw);
    if (!obj || typeof obj !== "object" || Array.isArray(obj)) {
      return { rows: [], rawFallback: raw };
    }
    const rows = Object.keys(obj).sort().map((k) => [k, String(obj[k])]);
    return { rows, rawFallback: null };
  } catch (_) {
    return { rows: [], rawFallback: raw };
  }
}

// Best-effort body-sample renderer. Returns:
//   { kind: "binary",  body: <marker> }              — `[BINARY n bytes]`
//   { kind: "json",    body: "{ pretty-printed }" }  — parsed JSON
//   { kind: "text",    body: <as-is> }               — anything else
const BINARY_PREFIX_RE = /^\[BINARY\b/;
function shapeBodySample(raw) {
  if (typeof raw !== "string" || raw.length === 0) return null;
  if (BINARY_PREFIX_RE.test(raw)) return { kind: "binary", body: raw };
  const trimmed = raw.trim();
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
    try {
      const parsed = JSON.parse(trimmed);
      return { kind: "json", body: JSON.stringify(parsed, null, 2) };
    } catch (_) {
      // fall through — show the raw sample as-is
    }
  }
  return { kind: "text", body: raw };
}

// Score → "62% match". `score` is on [0,1]; "high confidence" is >= 0.7
// per the task constraints (which then drives chip colour).
function formatScorePct(score) {
  const n = Number(score);
  if (!Number.isFinite(n)) return "—";
  const pct = Math.round(Math.max(0, Math.min(1, n)) * 100);
  return `${pct}% match`;
}
function isHighScore(score) {
  const n = Number(score);
  return Number.isFinite(n) && n >= 0.7;
}

// SmartParse rows ship `intent_tags` and `remediation` as already-
// stringified JSON (Phase 5b CP keeps them opaque). Operators
// shouldn't lose data when SmartParse changes its emit shape, so we
// gracefully fall back to displaying the raw text on parse failure.
function tryParseJsonString(raw) {
  if (typeof raw !== "string" || raw.length === 0) return { parsed: null, raw: null };
  try {
    return { parsed: JSON.parse(raw), raw: null };
  } catch (_) {
    return { parsed: null, raw };
  }
}

// Verdict / risk_level → chip palette. Keeps red for malicious/critical,
// orange for suspicious/high, green for benign/low, neutral otherwise.
function smartparseVerdictPalette(label) {
  const v = String(label || "").toLowerCase();
  if (v === "malicious" || v === "critical") {
    return { bg: "rgba(251,55,72,0.10)", fg: colors.red[200], border: "rgba(251,55,72,0.30)" };
  }
  if (v === "suspicious" || v === "high") {
    return { bg: "rgba(245,158,11,0.10)", fg: "#b45309", border: "rgba(245,158,11,0.30)" };
  }
  if (v === "benign" || v === "low") {
    return { bg: "rgba(31,193,107,0.10)", fg: colors.green[300], border: "rgba(31,193,107,0.30)" };
  }
  return { bg: "#F3F4F6", fg: TEXT_PRIMARY, border: BORDER_COLOR };
}

// Friendly category label for the header pill.
const CATEGORY_PRESET = {
  "auth-events":            { label: "Auth",    Icon: Lock,         color: "#7a5b10", bg: "rgba(255,219,67,0.18)", border: "rgba(223,180,0,0.40)" },
  "spec-validation-events": { label: "Spec",    Icon: FileWarning,  color: colors.primary[500], bg: "rgba(99,113,247,0.10)", border: "rgba(99,113,247,0.30)" },
  "graphql-events":         { label: "GraphQL", Icon: Code2,        color: colors.green[300], bg: "rgba(31,193,107,0.10)", border: "rgba(31,193,107,0.30)" },
  "api-observations":       { label: "API",     Icon: Activity,     color: colors.red[200], bg: "rgba(251,55,72,0.10)", border: "rgba(251,55,72,0.30)" },
};

// Convert primary_stream + primary row → (category, kind) for the
// eventMeta lookup. Mirrors what Phase 1A's CP route promises and what
// pingora's ctx structs put on the CH columns.
function deriveCategoryAndKind(primaryStream, primary) {
  if (!primaryStream || !primary) return { category: null, kind: null };
  // failure / error wins. result acts as the success-side fallback so
  // pass-through rows get a meaningful key. api-observations also keys
  // off http_<status> when blocked with no named reason, and falls back
  // to allowed when not blocked.
  if (primaryStream === "auth-events") {
    const k = primary.error_kind || primary.result || null;
    return { category: "auth", kind: k };
  }
  if (primaryStream === "spec-validation-events") {
    const k = primary.failure_kind || primary.result || null;
    return { category: "spec", kind: k };
  }
  if (primaryStream === "graphql-events") {
    const k = primary.failure_kind || primary.result || null;
    return { category: "graphql", kind: k };
  }
  if (primaryStream === "api-observations") {
    let k = primary.block_reason || null;
    if ((!k || k === "") && Number(primary.blocked) === 1) {
      const s = Number(primary.response_status);
      if (Number.isFinite(s) && s > 0) k = `http_${s}`;
    }
    if (!k && Number(primary.blocked) === 0) k = "allowed";
    return { category: "api", kind: k };
  }
  return { category: null, kind: null };
}

// Best-effort src_ip / identity extraction across all four contexts.
function pickSrcIp(ctx, primary) {
  if (!ctx) return null;
  const a = ctx.auth_event && ctx.auth_event.src_ip;
  const g = ctx.graphql_event && ctx.graphql_event.src_ip;
  const o = ctx.api_observation && ctx.api_observation.src_ip;
  const s = ctx.spec_validation_event && ctx.spec_validation_event.src_ip;
  const p = primary && primary.src_ip;
  return a || g || o || s || p || null;
}

function pickNetworkInfo(ctx, primary, ipReputation) {
  const sp = ctx?.smartparse_event;
  const auth = ctx?.auth_event;
  const apiObs = ctx?.api_observation;

  const sourceIp = pickSrcIp(ctx, primary);
  const sourcePort = sp?.source_port ?? primary?.source_port ?? null;
  const ja3Fingerprint =
    auth?.ja3 ||
    sp?.ja3 ||
    sp?.fp ||
    primary?.tls_fingerprint ||
    primary?.ja3 ||
    apiObs?.tls_fingerprint ||
    null;

  const countryCode =
    ipReputation?.countryCode ||
    sp?.src_country ||
    auth?.src_country ||
    ctx?.graphql_event?.src_country ||
    ctx?.spec_validation_event?.src_country ||
    primary?.src_country ||
    apiObs?.src_country ||
    null;

  const country = ipReputation?.countryName || countryCode || null;
  const asn = sp?.asn || primary?.src_asn || apiObs?.src_asn || null;

  return {
    sourceIp,
    sourcePort: sourcePort != null && sourcePort !== "" && Number(sourcePort) !== 0 ? String(sourcePort) : null,
    ja3Fingerprint,
    country,
    countryCode,
    asn: asn != null && asn !== "" && Number(asn) !== 0 ? String(asn) : null,
    city: sp?.city || null,
    region: sp?.region || null,
    timezone: sp?.timezone || null,
  };
}

function pickIdentity(ctx, primary) {
  if (!ctx) return null;
  const fromObs = ctx.api_observation;
  const fromAuth = ctx.auth_event;
  if (fromAuth && fromAuth.identity_id) {
    return {
      id: fromAuth.identity_id,
      kind: fromAuth.identity_kind || null,
      idp: fromAuth.identity_idp || null,
    };
  }
  if (fromObs && fromObs.identity_id) {
    return {
      id: fromObs.identity_id,
      kind: fromObs.identity_kind || null,
      idp: fromObs.identity_idp || null,
    };
  }
  if (primary && primary.identity_id) {
    return {
      id: primary.identity_id,
      kind: primary.identity_kind || null,
      idp: primary.identity_idp || null,
    };
  }
  return null;
}

// ─── Severity chip ───────────────────────────────────────────────────
function SeverityChip({ severity }) {
  const p = SEVERITY_PALETTE[severity] || SEVERITY_PALETTE.medium;
  return (
    <Chip
      label={p.label}
      size="small"
      sx={{
        ...tagChipSx,
        backgroundColor: p.bg,
        color: p.fg,
        border: `1px solid ${p.border}`,
      }}
    />
  );
}

// ─── Drawer ──────────────────────────────────────────────────────────
export default function AsecEventDrawer({
  open,
  onClose,
  event,
  siteId,
  categoryMeta = {},
}) {
  const navigate = useNavigate();

  const [detail, setDetail] = React.useState(null);
  const [state, setState]   = React.useState("idle"); // idle | loading | ok | notfound | error
  const [snackOpen, setSnackOpen] = React.useState(false);

  const requestId = event?.request_id || null;

  // Fetch on open. Only fires when we have both a request_id and a
  // siteId — defends against the parent forgetting to pass siteId.
  React.useEffect(() => {
    if (!open || !requestId || !siteId) {
      setDetail(null);
      setState("idle");
      return;
    }
    let cancelled = false;
    setState("loading");
    setDetail(null);
    asec.eventDetail(siteId, requestId)
      .then((r) => {
        if (cancelled) return;
        const body = r?.data;
        if (!body || body.found === false) {
          setState("notfound");
          setDetail(null);
        } else {
          setDetail(body);
          setState("ok");
        }
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 404) {
          setState("notfound");
          setDetail(null);
        } else {
          setState("error");
          setDetail(null);
        }
      });
    // eslint-disable-next-line consistent-return
    return () => { cancelled = true; };
  }, [open, requestId, siteId]);

  const handleCopy = (value) => {
    if (!nonEmpty(value)) return;
    copy(String(value));
    setSnackOpen(true);
    setTimeout(() => setSnackOpen(false), 2000);
  };

  // ── Header derivations ────────────────────────────────────────────
  const primaryStream = detail?.primary_stream || event?.primary_stream || null;
  const primary       = detail?.primary || event || null;
  const context       = detail?.context || {};

  const preset = CATEGORY_PRESET[primaryStream] || null;
  // Fall back to the parent's CategoryMeta only when we couldn't infer
  // a primary stream (e.g. DLP-only row).
  const fallbackMeta = event ? (categoryMeta[event.category] || null) : null;
  const headerMeta = preset || fallbackMeta || {
    label: "Event",
    Icon: ShieldOutlinedIcon,
    color: TEXT_SECONDARY,
    bg: "#F3F4F6",
    border: BORDER_COLOR,
  };
  const HeaderIcon = headerMeta.Icon;

  const headerPath =
    context?.spec_validation_event?.path_template ||
    primary?.path_template ||
    primary?.raw_path ||
    primary?.path ||
    event?.path ||
    "";
  const headerMethod = (primary?.method || event?.method || "").toUpperCase();

  // ── Canned meta lookup ────────────────────────────────────────────
  const { category, kind } = deriveCategoryAndKind(primaryStream, primary);
  const meta = lookupEventMeta(category, kind);

  // ── Deep-link helpers for Similar Events ──────────────────────────
  const navWithParams = (params) => {
    const qs = new URLSearchParams(params).toString();
    onClose && onClose();
    navigate(`/v2/api-security/events?${qs}`);
  };

  const srcIp   = pickSrcIp(context, primary);
  const identity = pickIdentity(context, primary);
  const ipReputation = detail?.ip_reputation || null;
  const networkInfo = React.useMemo(
    () => pickNetworkInfo(context, primary, ipReputation),
    [context, primary, ipReputation],
  );

  // ── Conditional flags ─────────────────────────────────────────────
  const dlpRows = Array.isArray(context?.dlp_audit) ? context.dlp_audit : [];

  const showIdentity      = !!identity;
  const showSpecOp        = !!(context?.spec_validation_event && (
    context.spec_validation_event.spec_id ||
    context.spec_validation_event.operation_id ||
    context.spec_validation_event.path_template
  ));
  const showGraphqlOp     = !!(context?.graphql_event && (
    context.graphql_event.operation_type ||
    context.graphql_event.operation_name ||
    nonEmpty(context.graphql_event.depth_observed) ||
    nonEmpty(context.graphql_event.complexity_observed)
  ));
  const showDlp           = dlpRows.length > 0;

  const tlsFingerprint = primary?.tls_fingerprint || primary?.ja3 || context?.api_observation?.ja3 || null;
  const requestFields = [
    (primary?.host || event?.host)
      ? {
        key: "host",
        label: "Host",
        value: primary?.host || event?.host,
        valueColor: PRIMARY_COLOR,
        copyable: true,
      }
      : null,
    headerMethod ? { key: "method", label: "Method", value: headerMethod } : null,
    headerPath
      ? {
        key: "path",
        label: "Path",
        value: headerPath,
        copyable: true,
        monospace: true,
      }
      : null,
    primary?.http_version ? { key: "http", label: "HTTP", value: primary.http_version } : null,
    primary?.content_type ? { key: "contentType", label: "Content-Type", value: primary.content_type } : null,
    nonEmpty(primary?.request_size)
      ? { key: "requestSize", label: "Request size", value: fmtBytes(primary.request_size) }
      : null,
    primary?.user_agent_class
      ? { key: "uaClass", label: "User-Agent class", value: primary.user_agent_class }
      : null,
    tlsFingerprint
      ? {
        key: "tls",
        label: "TLS fingerprint",
        value: tlsFingerprint,
        copyable: true,
        monospace: true,
      }
      : null,
  ].filter(Boolean);
  const showRequestDetails = drawerFieldsHaveContent(requestFields);

  const responseFields = [
    nonEmpty(primary?.response_status)
      ? { key: "status", label: "Status", variant: "status", value: primary.response_status }
      : null,
    nonEmpty(primary?.response_size)
      ? { key: "responseSize", label: "Response size", value: fmtBytes(primary.response_size) }
      : null,
    nonEmpty(primary?.latency_us)
      ? { key: "latency", label: "Latency", value: fmtMs(primary.latency_us) }
      : null,
    primary?.content_type
      ? { key: "contentType", label: "Content-Type", value: primary.content_type }
      : null,
  ].filter(Boolean);
  const showResponseDetails = drawerFieldsHaveContent(responseFields);

  // ── Phase 4a — redacted headers + body samples ────────────────────
  // The Phase 4a pingora branch added `request_headers_json`,
  // `response_headers_json`, `request_body_sample`, and
  // `response_body_sample` to every CH stream (with two extras on
  // graphql-events + spec-validation-events handled below). The
  // best-effort source order is primary → context-rows so we surface
  // whichever stream actually captured the snapshot for this request.
  const pickFirst = (...candidates) => {
    for (const c of candidates) {
      if (c && typeof c === "string" && c.length > 0) return c;
    }
    return null;
  };
  const ctxStreams = [
    context?.api_observation,
    context?.spec_validation_event,
    context?.graphql_event,
    context?.auth_event,
  ];
  const requestHeadersRaw = pickFirst(
    primary?.request_headers_json,
    ...ctxStreams.map((s) => s?.request_headers_json),
  );
  const responseHeadersRaw = pickFirst(
    primary?.response_headers_json,
    ...ctxStreams.map((s) => s?.response_headers_json),
  );
  const requestBodyRaw = pickFirst(
    primary?.request_body_sample,
    ...ctxStreams.map((s) => s?.request_body_sample),
  );
  const responseBodyRaw = pickFirst(
    primary?.response_body_sample,
    ...ctxStreams.map((s) => s?.response_body_sample),
  );
  const requestHeadersParsed  = React.useMemo(() => parseHeadersJson(requestHeadersRaw),  [requestHeadersRaw]);
  const responseHeadersParsed = React.useMemo(() => parseHeadersJson(responseHeadersRaw), [responseHeadersRaw]);
  const requestBodyShaped     = React.useMemo(() => shapeBodySample(requestBodyRaw),       [requestBodyRaw]);
  const responseBodyShaped    = React.useMemo(() => shapeBodySample(responseBodyRaw),      [responseBodyRaw]);
  const showRequestHeaders    = !!(requestHeadersParsed.rows.length > 0 || requestHeadersParsed.rawFallback);
  const showResponseHeaders   = !!(responseHeadersParsed.rows.length > 0 || responseHeadersParsed.rawFallback);
  const showRequestBody       = !!requestBodyShaped;
  const showResponseBody      = !!responseBodyShaped;

  // GraphQL variables + spec actual-value / expected-type — extra
  // Phase 4a columns on their respective streams.
  const graphqlVarsRaw  = context?.graphql_event?.variables_sample || null;
  const graphqlVarsShaped = React.useMemo(() => shapeBodySample(graphqlVarsRaw), [graphqlVarsRaw]);
  const specActualValue = context?.spec_validation_event?.actual_value_sample || null;
  const specExpectedTy  = context?.spec_validation_event?.expected_type || null;

  // ── Phase 5a — nearest_paths "Did you mean?" ──────────────────────
  // Top 3 closest matched operations, each `{path_template, method, score}`.
  const nearestPaths = React.useMemo(() => {
    const raw = context?.spec_validation_event?.nearest_paths;
    if (!raw || typeof raw !== "string") return [];
    try {
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr : [];
    } catch (_) {
      return [];
    }
  }, [context]);
  const showNearestPaths = nearestPaths.length > 0;

  // ── Phase 5b — SmartParse Threat Context enrichment ───────────────
  const sp = context?.smartparse_event || null;
  const spVerdictPalette = sp ? smartparseVerdictPalette(sp.verdict) : null;
  const spRiskPalette    = sp ? smartparseVerdictPalette(sp.risk_level) : null;
  const spAttackTypes    = sp && Array.isArray(sp.attack_types) ? sp.attack_types.filter(Boolean) : [];
  const spIntentParsed   = sp ? tryParseJsonString(sp.intent_tags)  : { parsed: null, raw: null };
  const spRemedParsed    = sp ? tryParseJsonString(sp.remediation)  : { parsed: null, raw: null };
  const showSmartParse   = !!sp;

  // Threat-context card show flag — keep showing the legacy IP/ASN/UA
  // panel even when only SmartParse lit up (so we have one consolidated
  // card instead of two siblings doing similar jobs).
  const showNetworkInfo = !!(networkInfo?.sourceIp || networkInfo?.country || networkInfo?.ja3Fingerprint);
  const networkFlagCode = networkInfo?.countryCode
    || (networkInfo?.country && String(networkInfo.country).length === 2 ? String(networkInfo.country) : null);
  const showThreatIntel = hasIpReputationEnrichment(ipReputation);
  const showNetworkSection = showNetworkInfo || showThreatIntel;
  const showThreatContext = !!(
    showSmartParse ||
    nonEmpty(primary?.anomaly_score) ||
    (Array.isArray(primary?.anomaly_reasons) && primary.anomaly_reasons.length > 0)
  );

  const opForSimilar = headerPath;
  const failureKey   = kind || primary?.failure_kind || primary?.error_kind || primary?.block_reason || null;
  const showSimilarOp        = !!(primary?.host && headerMethod && opForSimilar);
  const showSimilarFailure   = !!failureKey;
  const showSimilarIp        = !!srcIp;
  const showSimilarIdentity  = !!(identity?.id);
  const showSimilarEvents    = showSimilarOp || showSimilarFailure || showSimilarIp || showSimilarIdentity;
  const showRecommendations  = !!(meta?.recommendations && meta.recommendations.length > 0);

  // ── Raw JSON for the "Raw Event" section ──────────────────────────
  const rawJson = detail
    ? JSON.stringify(detail, null, 2)
    : event
      ? JSON.stringify(event, null, 2)
      : "";

  const rawDetail = detail || event;
  const summaryCells = state === "ok" && primary ? [
    {
      key: "timestamp",
      label: "Timestamp",
      value: formatDate(primary?.ts || event?.ts) || null,
      icon: Clock,
    },
    {
      key: "category",
      label: "Category",
      value: headerMeta.label,
      icon: Tag,
    },
    kind ? { key: "kind", label: "Kind", value: kind, icon: AlertCircle } : null,
    primary?.failure_field
      ? { key: "failure_field", label: "Failure field", value: primary.failure_field, icon: FileWarningIcon }
      : null,
  ].filter(Boolean) : [];

  const drawerTitle = meta?.title || `${headerMeta.label || "Security"} Event`;

  return (
    <>
    <WafEventDrawerShell
      open={open}
      onClose={onClose}
      title={drawerTitle}
      headerVariant="hero"
      loading={Boolean(event && state === "loading")}
      headerContent={event ? (
        <SmartParseDrawerHeader
          embedded
          title={drawerTitle}
          eventId={requestId}
          method={headerMethod}
          path={headerPath}
          onCopyEventId={handleCopy}
        />
      ) : null}
    >
      {!event && (
                  <Typography sx={{ fontSize: 14, color: TEXT_SECONDARY }}>
                    No event selected.
                  </Typography>
                )}

                {/* 404 — event aged out (or never existed) */}
                {event && state === "notfound" && (
                  <SectionCard icon={DETECTION_ICONS.info} title="Event not found" collapsible={false}>
                    <Stack spacing={1}>
                      <Typography sx={ { fontSize: 14, color: TEXT_PRIMARY }}>
                        We couldn't find this event in any of the security streams. It may have aged out of the retention window, or only its summary row exists.
                      </Typography>
                      {requestId && (
                        <DrawerFieldStack
                          columns={1}
                          onCopy={handleCopy}
                          fields={[{ key: "requestId", label: "Request ID", value: requestId, copyable: true, monospace: true }]}
                        />
                      )}
                    </Stack>
                  </SectionCard>
                )}

                {/* Error — show the raw row we already have so the user
                    isn't blocked from at least seeing what they clicked. */}
                {event && state === "error" && (
                  <SectionCard icon={DETECTION_ICONS.info} title="Could not load event" collapsible={false}>
                    <Stack spacing={1}>
                      <Typography sx={ { fontSize: 14, color: TEXT_PRIMARY }}>
                        The detail endpoint returned an error. Showing the table row we already had — see Raw Event below.
                      </Typography>
                      {requestId && (
                        <DrawerFieldStack
                          columns={1}
                          onCopy={handleCopy}
                          fields={[{ key: "requestId", label: "Request ID", value: requestId, copyable: true, monospace: true }]}
                        />
                      )}
                    </Stack>
                  </SectionCard>
                )}

                {/* ─── OK state — render every conditional card ───── */}
                {event && state === "ok" && (
                  <>
                    {/* Event Summary */}
                    <SectionCard icon={DETECTION_ICONS.detection} title="Event Summary">
                      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2, flexWrap: "wrap", gap: 1 }}>
                        <Chip
                          label={headerMeta.label}
                          size="small"
                          sx={{
                            ...tagChipSx,
                            backgroundColor: headerMeta.bg || "#EEF0FE",
                            color: headerMeta.color || PRIMARY_COLOR,
                            border: `1px solid ${headerMeta.border || "#E5E7FF"}`,
                          }}
                        />
                        {meta?.severity && <SeverityChip severity={meta.severity} />}
                        {primary?.result && (
                          <Chip
                            label={primary.result}
                            size="small"
                            sx={{ ...tagChipSx, backgroundColor: "#F3F4F6", color: TEXT_PRIMARY }}
                          />
                        )}
                        {Number(primary?.blocked) === 1 && (
                          <Chip
                            label="Blocked"
                            size="small"
                            sx={{
                              ...tagChipSx,
                              backgroundColor: "rgba(251,55,72,0.10)",
                              color: colors.red[200],
                              border: `1px solid rgba(251,55,72,0.30)`,
                            }}
                          />
                        )}
                      </Stack>

                      <Stack spacing={2}>
                        <EventAssessGrid cells={summaryCells} />
                      </Stack>
                    </SectionCard>

                    {/* What happened */}
                    <SectionCard icon={DETECTION_ICONS.info} title="What happened">
                      {meta ? (
                        <Stack spacing={1.5}>
                          <Typography sx={spSecondaryBodySx}>
                            {meta.what_happened}
                          </Typography>
                          {meta.docs_link && (
                            <Typography
                              component="a"
                              href={meta.docs_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={ { fontSize: 12, color: PRIMARY_COLOR, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                            >
                              Read more →
                            </Typography>
                          )}
                        </Stack>
                      ) : (
                        <Typography sx={ { fontSize: 14, color: TEXT_SECONDARY, fontStyle: "italic" }}>
                          {"This event kind is not documented yet. Pingora emitted a label the canned map does not recognise. The row raw verdict fields are visible in the Event Summary and Raw Event cards below."}
                        </Typography>
                      )}
                    </SectionCard>

                    {/* Recommended action */}
                    {showRecommendations && (
                    <SectionCard
                      icon={DETECTION_ICONS.match}
                      title="Recommended action"
                      count={meta.recommendations.length}
                    >
                      <DrawerNumberedList items={meta.recommendations} variant="rem" />
                    </SectionCard>
                    )}

                    {/* Request Details */}
                    {showRequestDetails && (
                    <SectionCard icon={DETECTION_ICONS.request} title="Request Details">
                      <DrawerFieldStack columns={2} onCopy={handleCopy} fields={requestFields} />
                    </SectionCard>
                    )}

                    {/* Request Headers — Phase 4a (redacted JSON map) */}
                    {showRequestHeaders && (
                      <SectionCard icon={KeyRound} title="Request Headers" defaultExpanded={false}>
                        <HeadersTable
                          rows={requestHeadersParsed.rows}
                          rawFallback={requestHeadersParsed.rawFallback}
                          onCopy={handleCopy}
                        />
                      </SectionCard>
                    )}

                    {/* Request Body Sample — Phase 4a (collapsed by default) */}
                    {showRequestBody && (
                      <SectionCard icon={FileInput} title="Request Body Sample" defaultExpanded={false}>
                        <BodySampleBlock shaped={requestBodyShaped} onCopy={handleCopy} />
                      </SectionCard>
                    )}

                    {/* Response Details */}
                    {showResponseDetails && (
                      <SectionCard icon={DETECTION_ICONS.response} title="Response Details">
                        <DrawerFieldStack columns={2} onCopy={handleCopy} fields={responseFields} />
                      </SectionCard>
                    )}

                    {/* Response Headers — Phase 4a (redacted JSON map) */}
                    {showResponseHeaders && (
                      <SectionCard icon={FileOutput} title="Response Headers" defaultExpanded={false}>
                        <HeadersTable
                          rows={responseHeadersParsed.rows}
                          rawFallback={responseHeadersParsed.rawFallback}
                          onCopy={handleCopy}
                        />
                      </SectionCard>
                    )}

                    {/* Response Body Sample — Phase 4a (collapsed by default) */}
                    {showResponseBody && (
                      <SectionCard icon={FileText} title="Response Body Sample" defaultExpanded={false}>
                        <BodySampleBlock shaped={responseBodyShaped} onCopy={handleCopy} />
                      </SectionCard>
                    )}

                    {/* Identity */}
                    {showIdentity && (
                      <SectionCard icon={DETECTION_ICONS.ja3} title="Identity">
                        <DrawerFieldStack
                          columns={2}
                          onCopy={handleCopy}
                          fields={[
                            identity.kind ? { key: "kind", label: "Identity kind", value: identity.kind } : null,
                            identity.id
                              ? {
                                key: "id",
                                label: "Identity ID (redacted)",
                                value: identity.id,
                                copyable: true,
                                monospace: true,
                              }
                              : null,
                            identity.idp ? { key: "idp", label: "IdP", value: identity.idp } : null,
                          ].filter(Boolean)}
                        />
                      </SectionCard>
                    )}

                    {/* Matched Spec Operation */}
                    {showSpecOp && (
                      <SectionCard icon={DETECTION_ICONS.match} title="Matched Spec Operation">
                        <Stack spacing={2}>
                          <DrawerFieldStack
                            columns={2}
                            onCopy={handleCopy}
                            fields={[
                              context.spec_validation_event.spec_id
                                ? { key: "specId", label: "Spec ID", value: context.spec_validation_event.spec_id, copyable: true }
                                : null,
                              context.spec_validation_event.operation_id
                                ? { key: "operationId", label: "Operation ID", value: context.spec_validation_event.operation_id }
                                : null,
                              context.spec_validation_event.path_template
                                ? { key: "pathTemplate", label: "Path template", value: context.spec_validation_event.path_template, copyable: true }
                                : null,
                              context.spec_validation_event.method
                                ? { key: "method", label: "Method", value: String(context.spec_validation_event.method).toUpperCase() }
                                : null,
                              context.spec_validation_event.failure_kind
                                ? { key: "failureKind", label: "Failure kind", value: context.spec_validation_event.failure_kind }
                                : null,
                              context.spec_validation_event.failure_field
                                ? { key: "failureField", label: "Failure field", value: context.spec_validation_event.failure_field }
                                : null,
                              specExpectedTy ? { key: "expectedType", label: "Expected type", value: specExpectedTy } : null,
                            ]}
                          />
                          {specActualValue && (
                            <Stack spacing={0.5}>
                              <Typography sx={ { fontSize: 14, color: TEXT_SECONDARY }}>
                                Actual value
                              </Typography>
                              <Box
                                component="code"
                                sx={{
                                  fontSize: 12,
                                  color: TEXT_PRIMARY,
                                  backgroundColor: colors.neutral[100],
                                  border: `1px solid ${BORDER_COLOR}`,
                                  borderRadius: "6px",
                                  px: 1,
                                  py: 0.5,
                                  display: "inline-block",
                                  maxWidth: "100%",
                                  overflowWrap: "anywhere",
                                  whiteSpace: "pre-wrap",
                                }}
                              >
                                {specActualValue}
                              </Box>
                            </Stack>
                          )}
                        </Stack>
                      </SectionCard>
                    )}

                    {/* Did you mean? — Phase 5a closest spec operations */}
                    {showNearestPaths && (
                      <SectionCard icon={Lightbulb} title="Did you mean? — Closest spec operations">
                        <Stack spacing={1.5}>
                          <Typography sx={ { fontSize: 12, color: TEXT_SECONDARY }}>
                            The path on this request didn't match any documented operation. The closest matches in the active spec are listed below — click one to jump to events for that operation.
                          </Typography>
                          <Stack spacing={1}>
                            {nearestPaths.map((np, i) => {
                              const m = String(np?.method || "").toUpperCase();
                              const pt = np?.path_template || "";
                              const sc = np?.score;
                              const high = isHighScore(sc);
                              return (
                                <Stack
                                  key={`${m}-${pt}-${i}`}
                                  direction="row"
                                  alignItems="center"
                                  spacing={1.5}
                                  sx={{
                                    p: 1.25,
                                    border: `1px solid ${BORDER_COLOR}`,
                                    borderRadius: "8px",
                                    cursor: pt ? "pointer" : "default",
                                    backgroundColor: "#FAFAFA",
                                    "&:hover": pt ? { backgroundColor: "#F3F4F6" } : {},
                                  }}
                                  onClick={() => {
                                    if (!pt) return;
                                    navWithParams({
                                      method: m,
                                      path_template: pt,
                                    });
                                  }}
                                >
                                  {m && (
                                    <Chip
                                      label={m}
                                      size="small"
                                      sx={{
                                        ...tagChipSx,
                                        backgroundColor: "rgba(99,113,247,0.10)",
                                        color: colors.primary[500],
                                      }}
                                    />
                                  )}
                                  <Typography
                                    sx={{
                                      flex: 1,
                                                                            fontSize: 14,
                                      color: TEXT_PRIMARY,
                                      overflowWrap: "anywhere",
                                    }}
                                  >
                                    {pt || "—"}
                                  </Typography>
                                  {nonEmpty(sc) && (
                                    <Chip
                                      label={formatScorePct(sc)}
                                      size="small"
                                      sx={{
                                        ...tagChipSx,
                                        backgroundColor: high ? "rgba(99,113,247,0.10)" : "#F3F4F6",
                                        color: high ? colors.primary[300] : colors.neutral[500],
                                        border: `1px solid ${high ? "rgba(99,113,247,0.30)" : BORDER_COLOR}`,
                                      }}
                                    />
                                  )}
                                </Stack>
                              );
                            })}
                          </Stack>
                        </Stack>
                      </SectionCard>
                    )}

                    {/* GraphQL Operation */}
                    {showGraphqlOp && (
                      <SectionCard icon={DETECTION_ICONS.match} title="GraphQL Operation">
                        <Stack spacing={2}>
                          <DrawerFieldStack
                            columns={2}
                            fields={[
                              context.graphql_event.operation_type
                                ? { key: "operationType", label: "Operation type", value: context.graphql_event.operation_type }
                                : null,
                              context.graphql_event.operation_name
                                ? { key: "operationName", label: "Operation name", value: context.graphql_event.operation_name }
                                : null,
                              nonEmpty(context.graphql_event.depth_observed)
                                ? { key: "depth", label: "Depth", value: String(context.graphql_event.depth_observed) }
                                : null,
                              nonEmpty(context.graphql_event.complexity_observed)
                                ? { key: "complexity", label: "Complexity", value: String(context.graphql_event.complexity_observed) }
                                : null,
                              nonEmpty(context.graphql_event.alias_count_observed)
                                ? { key: "aliases", label: "Aliases", value: String(context.graphql_event.alias_count_observed) }
                                : null,
                              nonEmpty(context.graphql_event.root_field_count)
                                ? { key: "rootFields", label: "Root fields", value: String(context.graphql_event.root_field_count) }
                                : null,
                              context.graphql_event.failure_kind
                                ? { key: "failureKind", label: "Failure kind", value: context.graphql_event.failure_kind }
                                : null,
                            ]}
                          />
                          {graphqlVarsShaped && (
                            <Stack spacing={0.75}>
                              <Typography sx={ { fontSize: 14, color: TEXT_SECONDARY }}>
                                Variables
                              </Typography>
                              <BodySampleBlock shaped={graphqlVarsShaped} onCopy={handleCopy} />
                            </Stack>
                          )}
                        </Stack>
                      </SectionCard>
                    )}

                    {/* PII Detected */}
                    {showDlp && (
                      <SectionCard icon={DETECTION_ICONS.ipReputation} title={`PII Detected (${dlpRows.length})`}>
                        <Stack spacing={2}>
                          {dlpRows.map((d, i) => {
                            const cats = Array.isArray(d.categories)
                              ? d.categories.join(", ")
                              : (d.categories || "");
                            return (
                              <Box
                                key={i}
                                sx={{
                                  border: `1px solid ${BORDER_COLOR}`,
                                  borderRadius: "8px",
                                  p: 2,
                                  backgroundColor: "#FAFAFA",
                                }}
                              >
                                <Stack spacing={1.25}>
                                  <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                                    {d.direction && (
                                      <Chip
                                        label={d.direction}
                                        size="small"
                                        sx={{
                                          ...tagChipSx,
                                          backgroundColor: d.direction === "response" ? "rgba(124,58,237,0.10)" : "rgba(99,113,247,0.10)",
                                          color: d.direction === "response" ? "#7c3aed" : colors.primary[500],
                                        }}
                                      />
                                    )}
                                    {cats && (
                                      <Chip
                                        label={cats}
                                        size="small"
                                        sx={{ ...tagChipSx, backgroundColor: "#F3F4F6", color: TEXT_PRIMARY }}
                                      />
                                    )}
                                  </Stack>
                                  {d.sample && (
                                    <DrawerFieldStack
                                      columns={2}
                                      fields={[
                                        { key: "sample", label: "Sample", value: d.sample },
                                        d.replacement ? { key: "replacement", label: "Replacement", value: d.replacement } : null,
                                        d.policy_id ? { key: "policy", label: "Policy", value: d.policy_id } : null,
                                      ]}
                                    />
                                  )}
                                  {!d.sample && (d.replacement || d.policy_id) && (
                                    <DrawerFieldStack
                                      columns={2}
                                      fields={[
                                        d.replacement ? { key: "replacement", label: "Replacement", value: d.replacement } : null,
                                        d.policy_id ? { key: "policy", label: "Policy", value: d.policy_id } : null,
                                      ]}
                                    />
                                  )}
                                </Stack>
                              </Box>
                            );
                          })}
                        </Stack>
                      </SectionCard>
                    )}

                    {/* Network Information — matches WAF / SmartParse drawer */}
                    {showNetworkSection && (
                      <SectionCard icon={DETECTION_ICONS.ipReputation} title="Network Information">
                        <Stack spacing={2}>
                          {networkFlagCode && networkInfo?.sourceIp && (
                            <NetworkCountryFlagRow
                              countryCode={networkFlagCode}
                              country={networkInfo.country}
                              sourceIp={networkInfo.sourceIp}
                              requireSourceIp
                            />
                          )}
                          {showNetworkInfo && (
                            <NetworkInformationPanel
                              network={networkInfo}
                              onCopy={handleCopy}
                              showCountryFlag={false}
                            />
                          )}
                          {showThreatIntel && (
                            <IpReputationPanel
                              showReportsSummary
                              hideIpAddressField={!!showNetworkInfo}
                              ipReputation={{
                                ipAddress: networkInfo?.sourceIp || ipReputation.ipAddress,
                                isp: ipReputation.isp,
                                usageType: ipReputation.usageType,
                                countryCode: ipReputation.countryCode,
                                totalReports: ipReputation.totalReports,
                                abuseConfidenceScore: ipReputation.abuseConfidenceScore,
                              }}
                            />
                          )}
                        </Stack>
                      </SectionCard>
                    )}

                    {/* Threat Context — SmartParse + anomaly signals */}
                    {showThreatContext && (
                      <SectionCard icon={DETECTION_ICONS.ip} title="Threat Context">
                        <Stack spacing={2}>
                          {(nonEmpty(primary?.anomaly_score) || (Array.isArray(primary?.anomaly_reasons) && primary.anomaly_reasons.length > 0)) && (
                            <DrawerFieldStack
                              columns={2}
                              fields={[
                                nonEmpty(primary?.anomaly_score)
                                  ? { key: "anomalyScore", label: "Anomaly score", value: String(primary.anomaly_score) }
                                  : null,
                                Array.isArray(primary?.anomaly_reasons) && primary.anomaly_reasons.length > 0
                                  ? { key: "anomalyReasons", label: "Anomaly reasons", value: primary.anomaly_reasons.join(", ") }
                                  : null,
                              ]}
                            />
                          )}

                          {/* Phase 5b — SmartParse enrichment */}
                          {showSmartParse && (
                            <Box
                              sx={
                                nonEmpty(primary?.anomaly_score) ||
                                (Array.isArray(primary?.anomaly_reasons) && primary.anomaly_reasons.length > 0)
                                  ? { mt: 1, pt: 2, borderTop: `1px dashed ${BORDER_COLOR}` }
                                  : {}
                              }
                            >
                              <Stack spacing={2}>
                                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                                  <Typography sx={ { fontSize: 12, color: TEXT_SECONDARY, mr: 0.5 }}>
                                    SmartParse:
                                  </Typography>
                                  {sp.verdict && (
                                    <Chip
                                      label={`Verdict: ${sp.verdict}`}
                                      size="small"
                                      sx={{
                                        ...tagChipSx,
                                        backgroundColor: spVerdictPalette.bg,
                                        color: spVerdictPalette.fg,
                                        border: `1px solid ${spVerdictPalette.border}`,
                                      }}
                                    />
                                  )}
                                  {sp.risk_level && (
                                    <Chip
                                      label={`Risk: ${sp.risk_level}`}
                                      size="small"
                                      sx={{
                                        ...tagChipSx,
                                        backgroundColor: spRiskPalette.bg,
                                        color: spRiskPalette.fg,
                                        border: `1px solid ${spRiskPalette.border}`,
                                      }}
                                    />
                                  )}
                                  {nonEmpty(sp.confidence) && (
                                    <Chip
                                      label={`Confidence: ${(() => {
                                        const c = Number(sp.confidence);
                                        if (!Number.isFinite(c)) return String(sp.confidence);
                                        // SmartParse confidence usually arrives on [0,1]; tolerate [0,100]
                                        const pct = c <= 1 ? Math.round(c * 100) : Math.round(c);
                                        return `${pct}%`;
                                      })()}`}
                                      size="small"
                                      sx={{ ...tagChipSx, backgroundColor: "#F3F4F6", color: TEXT_PRIMARY }}
                                    />
                                  )}
                                </Stack>

                                {spAttackTypes.length > 0 && (
                                  <Stack spacing={0.75}>
                                    <Typography sx={ { fontSize: 14, color: TEXT_SECONDARY }}>
                                      Attack types
                                    </Typography>
                                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 0.75 }}>
                                      {spAttackTypes.map((at, i) => (
                                        <Chip
                                          key={`${at}-${i}`}
                                          label={String(at)}
                                          size="small"
                                          sx={{
                                            ...tagChipSx,
                                            backgroundColor: "rgba(251,55,72,0.08)",
                                            color: colors.red[200],
                                            border: `1px solid rgba(251,55,72,0.25)`,
                                          }}
                                        />
                                      ))}
                                    </Stack>
                                  </Stack>
                                )}

                                {(spIntentParsed.parsed || spIntentParsed.raw) && (
                                  <Stack spacing={0.75}>
                                    <Typography sx={ { fontSize: 14, color: TEXT_SECONDARY }}>
                                      Intent tags
                                    </Typography>
                                    <IntentTagsSummary parsed={spIntentParsed.parsed} raw={spIntentParsed.raw} />
                                  </Stack>
                                )}

                                {(spRemedParsed.parsed || spRemedParsed.raw) && (
                                  <SectionCard
                                    icon={DETECTION_ICONS.match}
                                    title="Remediation"
                                    defaultExpanded={false}
                                  >
                                    {spRemedParsed.parsed ? (
                                      <RemediationView remediation={spRemedParsed.parsed} />
                                    ) : (
                                      <Box
                                        sx={{
                                          backgroundColor: colors.neutral[100],
                                          borderRadius: "8px",
                                          p: 2,
                                          border: `1px solid ${BORDER_COLOR}`,
                                          maxHeight: 240,
                                          overflow: "auto",
                                        }}
                                      >
                                        <Typography
                                          component="pre"
                                          sx={{
                                                                                        fontSize: 12,
                                            color: TEXT_PRIMARY,
                                            whiteSpace: "pre-wrap",
                                            wordBreak: "break-all",
                                            margin: 0,
                                          }}
                                        >
                                          {spRemedParsed.raw}
                                        </Typography>
                                      </Box>
                                    )}
                                  </SectionCard>
                                )}
                              </Stack>
                            </Box>
                          )}
                        </Stack>
                      </SectionCard>
                    )}

                    {/* Similar Events */}
                    {showSimilarEvents && (
                    <SectionCard icon={DETECTION_ICONS.filter} title="Similar Events">
                      <Stack spacing={1.5}>
                        <Typography sx={ { fontSize: 12, color: TEXT_SECONDARY }}>
                          Open the Events list pre-filtered by one of the dimensions of this event.
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                          {showSimilarOp && (
                            <Button
                              size="small"
                              variant="outlined"
                              sx={secondaryButtonStyle}
                              onClick={() => navWithParams({
                                host:   primary.host,
                                method: headerMethod,
                                path:   opForSimilar,
                              })}
                            >
                              Same operation
                            </Button>
                          )}
                          {showSimilarFailure && (
                            <Button
                              size="small"
                              variant="outlined"
                              sx={secondaryButtonStyle}
                              onClick={() => navWithParams(
                                category === "auth"
                                  ? { error_kind: failureKey }
                                  : category === "api"
                                    ? { block_reason: failureKey }
                                    : { failure_kind: failureKey }
                              )}
                            >
                              Same failure
                            </Button>
                          )}
                          {showSimilarIp && (
                            <Button
                              size="small"
                              variant="outlined"
                              sx={secondaryButtonStyle}
                              onClick={() => navWithParams({ src_ip: srcIp })}
                            >
                              Same IP
                            </Button>
                          )}
                          {showSimilarIdentity && (
                            <Button
                              size="small"
                              variant="outlined"
                              sx={secondaryButtonStyle}
                              onClick={() => navWithParams({
                                identity_id:   identity.id,
                                identity_kind: identity.kind || "",
                              })}
                            >
                              Same identity
                            </Button>
                          )}
                        </Stack>
                      </Stack>
                    </SectionCard>
                    )}

                    {/* Raw Event — collapsed by default */}
                    <SectionCard icon={DETECTION_ICONS.info} title="Raw Event" defaultExpanded={false}>
                      <SmartParseRawJsonViewer
                        data={rawDetail}
                        onCopy={handleCopy}
                        filename="asec_event.json"
                      />
                    </SectionCard>
                  </>
                )}

                {/* Idle (drawer hasn't fetched yet, e.g. siteId missing) —
                    fall back to the raw row so users aren't blocked. */}
                {event && state === "idle" && (
                  <SectionCard icon={DETECTION_ICONS.info} title="Raw Event" defaultExpanded>
                    <SmartParseRawJsonViewer
                      data={event}
                      onCopy={handleCopy}
                      filename="asec_event.json"
                    />
                  </SectionCard>
                )}
    </WafEventDrawerShell>

    {snackOpen && (
      <Box
        sx={{
          position: "fixed",
          bottom: 80,
          right: 24,
          backgroundColor: TEXT_PRIMARY,
          color: "#fff",
          px: 2,
          py: 1,
          borderRadius: "8px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          zIndex: 1400,
        }}
      >
        <Typography sx={{ fontSize: 14, color: "#fff" }}>
          Copied to clipboard!
        </Typography>
      </Box>
    )}
    </>
  );
}

AsecEventDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  event: PropTypes.object,
  siteId: PropTypes.string,
  categoryMeta: PropTypes.object,
};

// Re-export so consumers can show the badge in lists too.
export { lookupEventMeta };

// Suppress an unused-import warning if ShieldAlert is dropped from the
// switch — keeping it imported lets future extensions reach for it
// without re-rummaging the icon set.
void ShieldAlert;

// ─── HeadersTable ────────────────────────────────────────────────────
// Renders the DLP-redacted header map as a compact two-column grid.
// "[REDACTED]" values get a subtly different chip so operators can see
// at a glance which headers were dropped vs. kept verbatim.
function HeadersTable({ rows, rawFallback, onCopy }) {
  if (rawFallback) {
    return (
      <Box
        sx={{
          backgroundColor: colors.neutral[100],
          borderRadius: "8px",
          p: 2,
          border: `1px solid ${BORDER_COLOR}`,
          maxHeight: 240,
          overflow: "auto",
        }}
      >
        <Typography
          component="pre"
          sx={{
                        fontSize: 12,
            color: TEXT_PRIMARY,
            whiteSpace: "pre-wrap",
            wordBreak: "break-all",
            margin: 0,
          }}
        >
          {rawFallback}
        </Typography>
      </Box>
    );
  }
  if (!rows || rows.length === 0) {
    return (
      <Typography sx={ { fontSize: 14, color: TEXT_SECONDARY, fontStyle: "italic" }}>
        No headers captured.
      </Typography>
    );
  }
  return (
    <Box
      sx={{
        border: `1px solid ${BORDER_COLOR}`,
        borderRadius: "8px",
        overflow: "hidden",
      }}
    >
      {rows.map(([k, v], i) => {
        const isRedacted = v === "[REDACTED]";
        return (
          <Stack
            key={k + i}
            direction="row"
            alignItems="flex-start"
            spacing={2}
            sx={{
              px: 2,
              py: 1,
              backgroundColor: i % 2 === 0 ? "#FAFAFA" : "#fff",
              borderTop: i === 0 ? "none" : `1px solid ${BORDER_COLOR}`,
            }}
          >
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 500,
                color: TEXT_PRIMARY,
                minWidth: 160,
                wordBreak: "break-all",
              }}
            >
              {k}
            </Typography>
            <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: 1 }}>
              {isRedacted ? (
                <Chip
                  label="[REDACTED]"
                  size="small"
                  sx={{
                    ...tagChipSx,
                    height: 22,
                    fontSize: 12,
                    backgroundColor: "rgba(245,158,11,0.10)",
                    color: "#b45309",
                    border: `1px solid rgba(245,158,11,0.30)`,
                  }}
                />
              ) : (
                <Typography
                  sx={{
                                        fontSize: 12,
                    color: TEXT_PRIMARY,
                    overflowWrap: "anywhere",
                  }}
                >
                  {v}
                </Typography>
              )}
              {!isRedacted && onCopy && (
                <IconButton
                  size="small"
                  onClick={() => onCopy(v)}
                  sx={{ p: 0.25, ml: 0.5, "&:hover": { backgroundColor: "#EEF0FE" } }}
                  aria-label={`Copy ${k}`}
                >
                  <LayerdIcon icon={DETECTION_ICONS.copy} size={12} color="primary" />
                </IconButton>
              )}
            </Box>
          </Stack>
        );
      })}
    </Box>
  );
}
HeadersTable.propTypes = {
  rows: PropTypes.array,
  rawFallback: PropTypes.string,
  onCopy: PropTypes.func,
};

// ─── BodySampleBlock ────────────────────────────────────────────────
// Renders a request/response body sample. Pretty-prints JSON when the
// input parses as JSON; renders the BINARY marker in italics; otherwise
// shows the raw text in a mono block. Adds a Copy button.
function BodySampleBlock({ shaped, onCopy }) {
  if (!shaped) return null;
  if (shaped.kind === "binary") {
    return (
      <Typography
        sx={{
          fontStyle: "italic",
          fontSize: 14,
          color: TEXT_SECONDARY,
        }}
      >
        {shaped.body}
      </Typography>
    );
  }
  return (
    <Stack spacing={1.25}>
      <Box
        sx={{
          backgroundColor: colors.neutral[100],
          borderRadius: "8px",
          p: 2,
          border: `1px solid ${BORDER_COLOR}`,
          maxHeight: 360,
          overflow: "auto",
        }}
      >
        <Typography
          component="pre"
          sx={{
                        fontSize: 12,
            color: TEXT_PRIMARY,
            whiteSpace: "pre-wrap",
            wordBreak: "break-all",
            margin: 0,
          }}
        >
          {shaped.body}
        </Typography>
      </Box>
      {onCopy && (
        <Button
          variant="outlined"
          startIcon={<LayerdIcon icon={DETECTION_ICONS.copy} size={14} color="primary" />}
          onClick={() => onCopy(shaped.body)}
          sx={{ ...secondaryButtonStyle, width: "fit-content" }}
        >
          Copy
        </Button>
      )}
    </Stack>
  );
}
BodySampleBlock.propTypes = {
  shaped: PropTypes.shape({ kind: PropTypes.string, body: PropTypes.string }),
  onCopy: PropTypes.func,
};

// ─── IntentTagsSummary ──────────────────────────────────────────────
// SmartParse emits `intent_tags` as a stringified JSON object or array.
// We render arrays as chips, objects as a compact "N signals" summary
// with a collapsed expander showing key:value pairs. Strings fall back
// to a plain text block.
function IntentTagsSummary({ parsed, raw }) {
  const [expanded, setExpanded] = React.useState(false);
  if (parsed && Array.isArray(parsed)) {
    if (parsed.length === 0) return null;
    return (
      <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ gap: 0.75 }}>
        {parsed.slice(0, 24).map((t, i) => (
          <Chip
            key={`${String(t)}-${i}`}
            label={String(t)}
            size="small"
            sx={{
              ...tagChipSx,
              height: 22,
              fontSize: 12,
              backgroundColor: "#F3F4F6",
              color: TEXT_PRIMARY,
            }}
          />
        ))}
        {parsed.length > 24 && (
          <Typography sx={ { fontSize: 12, color: TEXT_SECONDARY, alignSelf: "center" }}>
            +{parsed.length - 24} more
          </Typography>
        )}
      </Stack>
    );
  }
  if (parsed && typeof parsed === "object") {
    const entries = Object.entries(parsed);
    if (entries.length === 0) return null;
    return (
      <Stack spacing={1}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography sx={ { fontSize: 12, color: TEXT_PRIMARY }}>
            {entries.length} intent signal{entries.length === 1 ? "" : "s"}
          </Typography>
          <Button
            size="small"
            onClick={() => setExpanded((v) => !v)}
            sx={{ ...secondaryButtonStyle, py: 0.25, fontSize: 12 }}
          >
            {expanded ? "Hide" : "Show details"}
          </Button>
        </Stack>
        {expanded && (
          <Box
            sx={{
              backgroundColor: colors.neutral[100],
              borderRadius: "8px",
              p: 1.5,
              border: `1px solid ${BORDER_COLOR}`,
              maxHeight: 240,
              overflow: "auto",
            }}
          >
            {entries.map(([k, v]) => (
              <Stack key={k} direction="row" spacing={1.5} sx={{ py: 0.25 }}>
                <Typography sx={ { fontSize: 12, fontWeight: 500, color: TEXT_PRIMARY, minWidth: 140 }}>
                  {k}
                </Typography>
                <Typography
                  sx={{
                                        fontSize: 12,
                    color: TEXT_PRIMARY,
                    overflowWrap: "anywhere",
                  }}
                >
                  {typeof v === "object" ? JSON.stringify(v) : String(v)}
                </Typography>
              </Stack>
            ))}
          </Box>
        )}
      </Stack>
    );
  }
  // Fall back to raw text.
  const text = parsed !== null && parsed !== undefined ? String(parsed) : raw || "";
  return (
    <Typography sx={ { fontSize: 14, color: TEXT_PRIMARY, whiteSpace: "pre-wrap" }}>
      {text}
    </Typography>
  );
}
IntentTagsSummary.propTypes = {
  parsed: PropTypes.any,
  raw: PropTypes.string,
};

// ─── RemediationView ────────────────────────────────────────────────
// SmartParse remediation is operator-facing text — render arrays as
// bullets and objects as labelled blocks. Plain strings/numbers as
// paragraphs. Anything else falls back to JSON.stringify.
function RemediationView({ remediation }) {
  if (typeof remediation === "string") {
    return (
      <Typography sx={ { fontSize: 14, color: TEXT_PRIMARY, whiteSpace: "pre-wrap" }}>
        {remediation}
      </Typography>
    );
  }
  if (Array.isArray(remediation)) {
    if (remediation.length === 0) return null;
    return (
      <DrawerNumberedList
        items={remediation.map((entry) => (
          typeof entry === "string" ? entry : JSON.stringify(entry)
        ))}
        variant="rem"
      />
    );
  }
  if (remediation && typeof remediation === "object") {
    const entries = Object.entries(remediation);
    if (entries.length === 0) return null;
    return (
      <Stack spacing={1.25}>
        {entries.map(([k, v]) => (
          <Stack key={k} spacing={0.25}>
            <Typography sx={ { fontSize: 12, color: TEXT_SECONDARY }}>
              {k}
            </Typography>
            {Array.isArray(v) ? (
              <Stack component="ul" spacing={0.5} sx={{ pl: 2.5, m: 0 }}>
                {v.map((item, i) => (
                  <Typography
                    key={i}
                    component="li"
                    sx={ { fontSize: 14, color: TEXT_PRIMARY }}
                  >
                    {typeof item === "string" ? item : JSON.stringify(item)}
                  </Typography>
                ))}
              </Stack>
            ) : (
              <Typography sx={ { fontSize: 14, color: TEXT_PRIMARY, whiteSpace: "pre-wrap" }}>
                {typeof v === "object" ? JSON.stringify(v, null, 2) : String(v)}
              </Typography>
            )}
          </Stack>
        ))}
      </Stack>
    );
  }
  return (
    <Typography sx={ { fontSize: 14, color: TEXT_PRIMARY }}>
      {String(remediation)}
    </Typography>
  );
}
RemediationView.propTypes = {
  remediation: PropTypes.any,
};
